/**
 * Structural tuner state.
 *
 * Deliberately excludes measurements: pitches and levels arrive dozens of
 * times a second and are held in {@link liveReadings} instead, so a moving
 * needle never re-renders the tree. What lives here changes at human speed —
 * settings and transport state.
 *
 * Every setter that touches a persisted value writes the whole settings blob
 * back, so storage can never hold a half-updated combination.
 */

import { create } from 'zustand'
import {
  buildEdition,
  capabilitiesFor,
  DEMO_A4,
  DEMO_TUNING_ID,
  type Capabilities,
  type Edition,
} from '../edition'
import type { AudioInputDevice } from '../audio/devices'
import type { AudioErrorKind } from '../audio/errors'
import type { TimingMessage } from '../audio/messages'
import { toTransposition, type Notation } from '../music/notation'
import { tuningById, type Tuning } from '../music/tunings'
import type { BarLayout } from '../ui/tuner/layout'
import { liveReadings } from './live-readings'
import { loadSettings, normaliseA4, saveSettings, type Settings } from './settings'

export { A4_STEP, MAX_A4, MIN_A4 } from './settings'

/**
 * Lifecycle of the audio pipeline.
 *
 * `interrupted` is not a failure: the microphone is still ours, the browser has
 * simply stopped delivering audio — a call, Siri, the screen locking. It ends
 * with a tap rather than a restart.
 */
export type EngineStatus = 'idle' | 'starting' | 'listening' | 'interrupted' | 'error'

/** A failure with the classification the UI needs to give advice. */
export interface TunerError {
  readonly kind: AudioErrorKind
  readonly message: string
}

const edition = buildEdition()
const capabilities = capabilitiesFor(edition)

/**
 * Settings the running edition is allowed to hold.
 *
 * Applied on load as well as on every change: a stored blob can outlive the
 * build that wrote it, so a demo installed over a full version must not come
 * up in DADGAD at 432 Hz.
 */
function allowed(settings: Settings): Settings {
  return {
    ...settings,
    tuningId: capabilities.tunings ? settings.tuningId : DEMO_TUNING_ID,
    a4: capabilities.concertPitch ? settings.a4 : DEMO_A4,
    notation: capabilities.notation ? settings.notation : 'english',
    transposition: capabilities.transposition ? settings.transposition : 0,
  }
}

const initial = allowed(loadSettings())

interface TunerState {
  readonly status: EngineStatus
  readonly error: TunerError | null
  readonly devices: readonly AudioInputDevice[]
  readonly selectedDeviceId: string | null
  readonly timing: TimingMessage | null
  /**
   * Rate the AudioContext settled on, or null while stopped.
   *
   * Shown in the UI because it is the one number nothing else reveals and
   * everything downstream depends on: Safari picks between 44.1 and 48 kHz on
   * its own, and a reading that disagrees with another device is worth
   * checking against this before anything else.
   */
  readonly sampleRate: number | null
  readonly tuning: Tuning
  readonly a4: number
  readonly notation: Notation
  readonly transposition: number
  readonly layout: BarLayout
  /** Which build this is. */
  readonly edition: Edition
  /** What this build may do; see `src/edition.ts`. */
  readonly capabilities: Capabilities
  setStatus: (status: EngineStatus) => void
  setInterrupted: (interrupted: boolean) => void
  setError: (error: TunerError) => void
  setDevices: (devices: readonly AudioInputDevice[]) => void
  selectDevice: (deviceId: string | null) => void
  setTuning: (tuningId: string) => void
  setA4: (a4: number) => void
  setNotation: (notation: Notation) => void
  setTransposition: (semitones: number) => void
  setLayout: (layout: BarLayout) => void
  applyTiming: (timing: TimingMessage) => void
  setSampleRate: (sampleRate: number | null) => void
  reset: () => void
}

export const useTunerStore = create<TunerState>((set, get) => {
  /** Write the current settings back to storage after a change. */
  const persist = (): void => {
    const { tuning, a4, notation, transposition, layout, selectedDeviceId } = get()
    const settings: Settings = {
      tuningId: tuning.id,
      a4,
      notation,
      transposition,
      layout,
      deviceId: selectedDeviceId,
    }
    saveSettings(settings)
  }

  return {
    status: 'idle',
    error: null,
    devices: [],
    selectedDeviceId: initial.deviceId,
    timing: null,
    sampleRate: null,
    tuning: tuningById(initial.tuningId),
    a4: initial.a4,
    notation: initial.notation,
    transposition: initial.transposition,
    layout: initial.layout,
    edition,
    capabilities,

    setStatus: (status) => {
      set({ status, error: status === 'error' ? get().error : null })
    },

    /**
     * Move between listening and interrupted, and nowhere else.
     *
     * An interruption arriving while the tuner is stopped or already broken
     * says nothing worth showing, and must not resurrect either state.
     */
    setInterrupted: (interrupted) => {
      const { status } = get()
      if (interrupted && status === 'listening') {
        // Whatever is on the meters was measured before the audio stopped.
        liveReadings.clear()
        set({ status: 'interrupted' })
        return
      }
      if (!interrupted && status === 'interrupted') {
        set({ status: 'listening' })
      }
    },

    setError: (error) => {
      liveReadings.clear()
      set({ status: 'error', error })
    },

    setDevices: (devices) => {
      set({ devices })
    },

    selectDevice: (deviceId) => {
      set({ selectedDeviceId: deviceId })
      persist()
    },

    setTuning: (tuningId) => {
      if (!capabilities.tunings) return
      const tuning = tuningById(tuningId)
      liveReadings.reset(tuning.strings.length)
      set({ tuning })
      persist()
    },

    setA4: (a4) => {
      if (!capabilities.concertPitch) return
      set({ a4: normaliseA4(a4) })
      persist()
    },

    setNotation: (notation) => {
      if (!capabilities.notation) return
      set({ notation })
      persist()
    },

    setTransposition: (semitones) => {
      if (!capabilities.transposition) return
      set({ transposition: toTransposition(semitones) })
      persist()
    },

    setLayout: (layout) => {
      set({ layout })
      persist()
    },

    applyTiming: (timing) => {
      set({ timing })
    },

    setSampleRate: (sampleRate) => {
      set({ sampleRate })
    },

    reset: () => {
      liveReadings.clear()
      // The rate belongs to the context that has just been closed; keeping it
      // would claim a running pipeline that no longer exists.
      set({ status: 'idle', error: null, sampleRate: null, timing: null })
    },
  }
})
